// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@werner/MobiusOverrides/Button/Button";
import TextField from "@werner/MobiusOverrides/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { XNGAGE_CUSTOM_WIDGET } from "@werner/XDL/constants";
import Exclamation from "@werner/XDL/Icons/Exclamation";
import { doScroll, useValidate } from "@werner/XDL/Utilities";
import xDefaultRules from "@werner/XDL/Utilities/xValidate/xDefaultRules";
import parse from "html-react-parser";
import React, { RefObject, useContext, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

import { css } from "styled-components";

const enum fields {
    termsAndConditionsLink = "termsAndConditionsLink",
    customerNumberInvoiceTooltip = "customerNumberInvoiceTooltip",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsAndConditionsLink]: LinkFieldValue;
        [fields.customerNumberInvoiceTooltip]: string;
    };
}
const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    addAccount,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface RegisterAccountFormStyles {
    wrapper?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

const registerAccountFormStyles: RegisterAccountFormStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 20px;
        `,
    },
    tooltip: {
        mergeCss: true,
        cssOverrides: {
            tooltipContainer: css`
                max-width: 400px;
                min-width: unset;
            `,
        },
    },
};
const styles = registerAccountFormStyles;
const StyledForm = getStyledWrapper("form");
const formId = "RegisterAccountForm";
type CustomerForm = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    customerNumber: string;
    jobTitle: string;
    company: string;
    termsAccepted: boolean;
    isSubscribed: boolean;
};

const RegisterAccountForm = ({ fields, addAccount, validateReCaptcha }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const { url: termsAndConditionsUrl } = useGetLink(fields.termsAndConditionsLink);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const { field, errors, isSubmitting, handleSubmit, resetForm, setFieldValue, formik } = useValidate({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            customerNumber: "",
            jobTitle: "",
            company: "",
            termsAccepted: false,
            isSubscribed: false,
        },
        validators: {
            firstName: `required|${xDefaultRules.firstName}`,
            lastName: `required|${xDefaultRules.lastName}`,
            email: `required|${xDefaultRules.email}`,
            phoneNumber: `required|${xDefaultRules.phone}`,
            customerNumber: `required`,
            jobTitle: `required|${xDefaultRules.text}`,
            company: `required|${xDefaultRules.text}`,
            termsAccepted: `${xDefaultRules.requiredCheckBox}`,
        },
        onSubmit: (fieldValues: CustomerForm, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    /*
     * The following line is needed to scroll to invalid fields,
     * since Formik doesn't support this functionality out of the box.
     * This approach scrolls to the first invalid field when submitting
     * and when validation errors exist.
     */
    useEffect(() => {
        if (!Object.keys(errors).length || !isSubmitting) {
            return;
        }

        doScroll();
    }, [errors, isSubmitting]);

    const submitHandler = async (fieldValues: CustomerForm, setSubmitting: any) => {
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid || Object.keys(errors).length > 0) {
            setSubmitting(false);
            return;
        }

        const { email, firstName, lastName, isSubscribed, termsAccepted, ...rest } = fieldValues;
        addAccount({
            firstName,
            lastName,
            email,
            userName: email,
            isSubscribed,
            properties: { ...rest, termsAccepted, fromUserRegistration: "true" },
            onSuccess: () => {
                toasterContext.addToast({ messageType: "success", body: siteMessage("Werner_Registration_success") });
                resetForm();

                setSubmitting(false);
            },
            onError: (error: any) => {
                toasterContext.addToast({ messageType: "danger", body: error });
                setSubmitting(false);
            },
        } as any);
    };

    return (
        <StyledWrapper>
            <StyledForm {...styles.wrapper} noValidate onSubmit={handleSubmit}>
                <TextField
                    label={translate("Customer Number")}
                    placeholder={translate("Customer Number")}
                    renderHintAsIcon={true}
                    required={true}
                    hint={
                        !!fields.customerNumberInvoiceTooltip.length && (
                            <Tooltip
                                triggerComponent={<Icon src={Exclamation} size={16} />}
                                text={parse(fields.customerNumberInvoiceTooltip, parserOptions).toString()}
                                {...styles.tooltip}
                            ></Tooltip>
                        )
                    }
                    {...field("customerNumber")}
                    {...{ formId }}
                />
                <TextField
                    {...field("firstName")}
                    label={translate("First Name")}
                    placeholder={translate("First Name")}
                    {...{ formId }}
                />
                <TextField
                    {...field("lastName")}
                    label={translate("Last Name")}
                    placeholder={translate("Last Name")}
                    {...{ formId }}
                />
                <TextField
                    {...field("company")}
                    label={translate("Company")}
                    placeholder={translate("Company")}
                    {...{ formId }}
                />
                <TextField
                    {...field("jobTitle")}
                    label={translate("Job Title")}
                    placeholder={translate("Job Title")}
                    {...{ formId }}
                />
                <TextField
                    {...field("phoneNumber")}
                    label={translate("Phone Number")}
                    placeholder={translate("Phone Number")}
                    {...{ formId }}
                />
                <TextField
                    {...field("email")}
                    type="email"
                    label={translate("Email")}
                    placeholder={translate("Email")}
                    {...{ formId }}
                />
                <Checkbox
                    onChange={(_, value) => setFieldValue("isSubscribed", value)}
                    checked={field("isSubscribed").value}
                    data-test-selector="registration_Subscription"
                    {...{ formId }}
                >
                    {siteMessage("SignIn_Sign_Up_Newsletter")}
                </Checkbox>
                {termsAndConditionsUrl && (
                    <CheckboxGroup
                        error={field("termsAccepted").error && siteMessage("Werner_Registration_Terms_Is_Required")}
                        aria-invalid={!!field("termsAccepted").error}
                    >
                        <Checkbox
                            onChange={(_, value) => {
                                setFieldValue("termsAccepted", value);
                            }}
                            checked={field("termsAccepted").value}
                            data-test-selector="registration_Terms"
                            {...{ formId }}
                        >
                            {siteMessage("Werner_Registration_Terms", termsAndConditionsUrl)}
                        </Checkbox>
                    </CheckboxGroup>
                )}
                <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    data-test-selector="registration_createButton"
                >
                    {translate("Register")}
                </Button>
            </StyledForm>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(RegisterAccountForm),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Account Registration Form",
        allowedContexts: ["CreateAccountPage"],
        fieldDefinitions: [
            {
                name: fields.termsAndConditionsLink,
                fieldType: "Contextual",
                displayName: "Terms And Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: true,
            },
            {
                name: fields.customerNumberInvoiceTooltip,
                fieldType: "Contextual",
                displayName: "Customer Number Invoice Tooltip",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
